import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Checkbox from '@mui/material/Checkbox';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { getSshKeyListV4QueryKey, useSshKeyV2 } from '@newfold/huapi-js';
import { SshKeyV2Body } from '@newfold/huapi-js/src/index.schemas';

import { ReactComponent as SshKeyIcon } from '~/assets/images/sshkey-icon.svg';
import { useAlert } from '~/components/Alerts/alertsStore';
import { SEVERITY_COLORS } from '~/components/CardTopBanner/utils/constants';
import ResponsiveDialog, {
  StyledButton,
  StyledLoadingButton,
} from '~/components/ResponsiveDialog';
import ProcessingState from '~/components/Status/ProcessingState';
import SuccessState from '~/components/Status/SuccessState';
import useAccount from '~/hooks/useAccount';
import ModalHeader from '~/Nova/scenes/HostingList/scenes/SelfManagedHosting/scenes/SelfManagedHostingLayout/AiTooling/components/ManageApiKeysModal/ModalHeader';

type DeleteKeyOptionProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  keyName: string;
  keyId?: number;
  authorize: boolean;
  isRemoveAction?: boolean;
  onDelete?: (keyName: string, authorize: boolean) => void;
};

type DeleteKeyStatus = 'confirmation' | 'processing' | 'success';

const warningContainerSx = {
  width: 'fit-content',
  px: 1,
  py: 0.5,
  borderRadius: 1,
  backgroundColor: SEVERITY_COLORS.error.backgroundColor,
} as const;

const DeleteKey = ({
  open,
  setOpen,
  keyName,
  keyId = undefined,
  authorize,
  isRemoveAction = false,
  onDelete = undefined,
}: DeleteKeyOptionProps) => {
  const { t } = useTranslation('sshKeyManager', {
    keyPrefix: 'deleteKey',
  });

  const { id: hostingId } = useAccount();
  const generateAlert = useAlert();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const [isDeleteActionConfirmed, setIsDeleteActionConfirmed] = useState(false);
  const [status, setStatus] = useState<DeleteKeyStatus>('confirmation');

  const queryKey = getSshKeyListV4QueryKey(hostingId);

  const isProcessing = status === 'processing';
  const isSuccess = status === 'success';
  const isConfirmation = status === 'confirmation';

  const resetModalState = () => {
    setIsDeleteActionConfirmed(false);
    setStatus('confirmation');
  };

  const closeModal = () => {
    resetModalState();
    setOpen(false);
  };

  const handleClose = () => {
    if (isProcessing) {
      return;
    }

    closeModal();
  };

  const setAlertMessage = (variant: string, descKey: string) => {
    generateAlert({
      severity: variant,
      description: t(descKey, { keyName }),
      showCloseBtn: true,
    });
  };

  const getDeletePayload = (): SshKeyV2Body => {
    if (keyId) {
      return {
        id: keyId,
        remote: isRemoveAction ? 0 : 1,
      };
    }

    return {
      name: keyName,
      authorize,
    };
  };

  const { mutate: sshDelete } = useSshKeyV2({
    mutation: {
      onMutate: () => {
        queryClient.cancelQueries({
          queryKey,
        });
      },

      onSuccess: (data) => {
        if (!data?.data?.deleted) {
          setStatus('confirmation');
          setAlertMessage('error', 'deleteToast');
          return;
        }

        setIsDeleteActionConfirmed(false);
        setStatus('success');
      },

      onError: () => {
        setStatus('confirmation');
        setAlertMessage('error', 'deleteToast');
      },

      onSettled: async () => {
        await queryClient.invalidateQueries({
          queryKey,
        });
      },
    },
  });

  const handleDelete = () => {
    if (!isDeleteActionConfirmed || isProcessing) {
      return;
    }

    setIsDeleteActionConfirmed(false);

    if (onDelete) {
      onDelete(keyName, authorize);
      setStatus('success');
      return;
    }

    setStatus('processing');

    sshDelete({
      hostingId,
      data: getDeletePayload(),
    });
  };

  const renderContent = () => {
    if (isProcessing) {
      return (
        <ProcessingState
          title={t('processingTitle')}
          description={t('processingDescription')}
        />
      );
    }

    if (isSuccess) {
      return (
        <SuccessState
          title={t('successTitle')}
          description={t('successDescription')}
        />
      );
    }

    return (
      <Stack spacing={2.5}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={warningContainerSx}
        >
          <ErrorOutlineIcon color="error" sx={{ fontSize: 18 }} />

          <Typography variant="body2" color="error">
            {t('warning')}
          </Typography>
        </Stack>

        <Typography variant="body1">
          <Trans
            t={t}
            i18nKey="description"
            values={{ keyName }}
            components={{ strong: <strong /> }}
          />
        </Typography>

        <FormControlLabel
          sx={{ m: 0, gap: 0.5 }}
          control={
            <Checkbox
              checked={isDeleteActionConfirmed}
              onChange={(event) =>
                setIsDeleteActionConfirmed(event.target.checked)
              }
              sx={{
                color: 'primary.main',
                p: 0,
              }}
            />
          }
          label={<Typography variant="body2">{t('confirmation')}</Typography>}
        />
      </Stack>
    );
  };

  return (
    <ResponsiveDialog open={open} data-testid="ssh-delete-key-dialog">
      <ModalHeader
        icon={<SshKeyIcon />}
        title={isSuccess ? t('successHeader') : t('title', { keyName })}
        onClose={isProcessing ? undefined : handleClose}
      />

      <DialogContent>
        <Divider sx={{ mb: 2 }} />
        {renderContent()}
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: theme.palette.background.lightGrey,
        }}
      >
        {isConfirmation ? (
          <>
            <StyledButton variant="outlined" onClick={handleClose}>
              {t('cancelTxt')}
            </StyledButton>

            <StyledLoadingButton
              variant="contained"
              color="error"
              disabled={!isDeleteActionConfirmed}
              onClick={handleDelete}
            >
              {t('deleteBtn')}
            </StyledLoadingButton>
          </>
        ) : (
          <StyledButton
            variant="contained"
            onClick={handleClose}
            disabled={isProcessing}
          >
            {t('close')}
          </StyledButton>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default DeleteKey;
